import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { formatINR, formatINRFull, formatDate, apiErrorMsg } from '@/lib/formatters';
import { FileText, Plus, CheckCircle, Clock, XCircle, AlertTriangle } from 'lucide-react';

const BOOKING_STATUS_COLORS = {
  token_pending: 'bg-amber-100 text-amber-800',
  confirmed: 'bg-emerald-100 text-emerald-800',
  token_rejected: 'bg-red-100 text-red-800',
  cancelled: 'bg-slate-200 text-slate-700',
};

const TOKEN_STATUS_COLORS = {
  submitted: 'bg-blue-100 text-blue-800',
  verified: 'bg-emerald-100 text-emerald-800',
  rejected: 'bg-red-100 text-red-800',
};

export default function BookingsPage() {
  const { user } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [verifyBooking, setVerifyBooking] = useState(null);

  const fetchBookings = () => {
    setLoading(true);
    api.get('/bookings').then(r => { setBookings(r.data.bookings); setLoading(false); }).catch(() => setLoading(false));
  };

  useEffect(() => { fetchBookings(); }, []);

  const handleVerify = async (bookingId, decision) => {
    try {
      await api.put(`/bookings/${bookingId}`, { token_status: decision });
      toast.success(`Token ${decision}`);
      fetchBookings();
      setVerifyBooking(null);
    } catch (err) { toast.error(apiErrorMsg(err)); }
  };

  const totalValue = bookings.reduce((s, b) => s + (b.total_value || 0), 0);
  const totalReceived = bookings.reduce((s, b) => s + (b.amount_received || 0), 0);

  return (
    <div className="space-y-6" data-testid="bookings-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Bookings</h1>
          <p className="text-slate-500 text-sm mt-0.5">{bookings.length} bookings</p>
        </div>
        {user?.role !== 'finance' && (
          <Button onClick={() => setShowCreate(true)} className="bg-emerald-600 hover:bg-emerald-700" data-testid="create-booking-btn">
            <Plus size={16} /> New Booking
          </Button>
        )}
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-slate-200 kpi-card">
          <CardContent className="p-4">
            <p className="text-xs text-slate-400 uppercase tracking-wider mb-1">Total Bookings</p>
            <p className="text-2xl font-extrabold font-mono">{bookings.length}</p>
          </CardContent>
        </Card>
        <Card className="border-slate-200 kpi-card">
          <CardContent className="p-4">
            <p className="text-xs text-slate-400 uppercase tracking-wider mb-1">Booking Value</p>
            <p className="text-2xl font-extrabold font-mono text-emerald-700">{formatINR(totalValue)}</p>
          </CardContent>
        </Card>
        <Card className="border-slate-200 kpi-card">
          <CardContent className="p-4">
            <p className="text-xs text-slate-400 uppercase tracking-wider mb-1">Collected</p>
            <p className="text-2xl font-extrabold font-mono text-blue-700">{formatINR(totalReceived)}</p>
          </CardContent>
        </Card>
        <Card className="border-slate-200 kpi-card">
          <CardContent className="p-4">
            <p className="text-xs text-slate-400 uppercase tracking-wider mb-1">Pending Verification</p>
            <p className="text-2xl font-extrabold font-mono text-amber-600">{bookings.filter(b => b.token_status === 'submitted').length}</p>
          </CardContent>
        </Card>
      </div>

      {/* Bookings Table */}
      <div className="table-container bg-white">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead className="text-xs uppercase tracking-wider font-semibold">Customer</TableHead>
              <TableHead className="text-xs uppercase tracking-wider font-semibold">Project</TableHead>
              <TableHead className="text-xs uppercase tracking-wider font-semibold">Config</TableHead>
              <TableHead className="text-xs uppercase tracking-wider font-semibold">Total Value</TableHead>
              <TableHead className="text-xs uppercase tracking-wider font-semibold">Token</TableHead>
              <TableHead className="text-xs uppercase tracking-wider font-semibold">Token Status</TableHead>
              <TableHead className="text-xs uppercase tracking-wider font-semibold">Status</TableHead>
              <TableHead className="text-xs uppercase tracking-wider font-semibold">Date</TableHead>
              {['director', 'finance'].includes(user?.role) && (
                <TableHead className="text-xs uppercase tracking-wider font-semibold">Action</TableHead>
              )}
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={i}>{Array.from({ length: 9 }).map((_, j) => <TableCell key={j}><div className="h-4 bg-slate-100 rounded animate-pulse" /></TableCell>)}</TableRow>
              ))
            ) : bookings.length === 0 ? (
              <TableRow><TableCell colSpan={9} className="text-center py-12 text-slate-400">No bookings yet</TableCell></TableRow>
            ) : (
              bookings.map(b => (
                <TableRow key={b._id} data-testid={`booking-row-${b._id}`}>
                  <TableCell>
                    <div>
                      <p className="font-semibold text-sm">{b.lead_name}</p>
                      <p className="text-xs text-slate-400">{b.lead_id}</p>
                    </div>
                  </TableCell>
                  <TableCell className="text-sm">{b.project_name}</TableCell>
                  <TableCell className="text-sm">{b.configuration}</TableCell>
                  <TableCell className="text-sm font-semibold">{formatINRFull(b.total_value)}</TableCell>
                  <TableCell className="text-sm">{formatINRFull(b.token_amount)}</TableCell>
                  <TableCell>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${TOKEN_STATUS_COLORS[b.token_status] || 'bg-slate-100'}`}>
                      {b.token_status}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${BOOKING_STATUS_COLORS[b.status] || 'bg-slate-100'}`}>
                      {b.status?.replace(/_/g, ' ')}
                    </span>
                  </TableCell>
                  <TableCell className="text-xs text-slate-500">{formatDate(b.booking_date)}</TableCell>
                  {['director', 'finance'].includes(user?.role) && (
                    <TableCell>
                      {b.token_status === 'submitted' && (
                        <div className="flex items-center gap-1">
                          <Button size="sm" variant="ghost" className="h-7 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50" onClick={() => handleVerify(b._id, 'verified')} data-testid={`verify-booking-${b._id}`}>
                            <CheckCircle size={14} /> Verify
                          </Button>
                          <Button size="sm" variant="ghost" className="h-7 text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => handleVerify(b._id, 'rejected')} data-testid={`reject-booking-${b._id}`}>
                            <XCircle size={14} /> Reject
                          </Button>
                        </div>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {showCreate && <CreateBookingDialog open onClose={() => setShowCreate(false)} onCreated={() => { fetchBookings(); setShowCreate(false); }} />}
    </div>
  );
}

function CreateBookingDialog({ open, onClose, onCreated }) {
  const [projects, setProjects] = useState([]);
  const [form, setForm] = useState({
    lead_id: '', lead_name: '', customer_phone: '', customer_email: '',
    project_id: '', project_name: '', unit_id: '', configuration: '',
    base_price: '', other_charges: '', tax: '', total_value: '',
    token_amount: '', token_mode: 'NEFT', token_utr: '', remarks: '',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/projects').then(r => setProjects(r.data.projects)).catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const proj = projects.find(p => p._id === form.project_id);
      await api.post('/bookings', {
        ...form,
        project_name: proj?.name || '',
        base_price: Number(form.base_price) || 0,
        other_charges: Number(form.other_charges) || 0,
        tax: Number(form.tax) || 0,
        total_value: Number(form.total_value) || 0,
        token_amount: Number(form.token_amount) || 0,
      });
      toast.success('Booking created');
      onCreated();
    } catch (err) { toast.error(apiErrorMsg(err)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>New Booking</DialogTitle></DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Lead ID</Label><Input value={form.lead_id} onChange={e => setForm({ ...form, lead_id: e.target.value })} placeholder="VIR-0001" data-testid="booking-lead-id" /></div>
            <div><Label>Customer Name *</Label><Input value={form.lead_name} onChange={e => setForm({ ...form, lead_name: e.target.value })} required data-testid="booking-customer-name" /></div>
            <div><Label>Phone</Label><Input value={form.customer_phone} onChange={e => setForm({ ...form, customer_phone: e.target.value })} /></div>
            <div><Label>Email</Label><Input value={form.customer_email} onChange={e => setForm({ ...form, customer_email: e.target.value })} /></div>
            <div>
              <Label>Project</Label>
              <Select value={form.project_id} onValueChange={v => setForm({ ...form, project_id: v })}>
                <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{projects.map(p => <SelectItem key={p._id} value={p._id}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Configuration</Label><Input value={form.configuration} onChange={e => setForm({ ...form, configuration: e.target.value })} /></div>
            <div><Label>Base Price</Label><Input type="number" value={form.base_price} onChange={e => setForm({ ...form, base_price: e.target.value })} /></div>
            <div><Label>Other Charges</Label><Input type="number" value={form.other_charges} onChange={e => setForm({ ...form, other_charges: e.target.value })} /></div>
            <div><Label>Tax</Label><Input type="number" value={form.tax} onChange={e => setForm({ ...form, tax: e.target.value })} /></div>
            <div><Label>Total Value *</Label><Input type="number" value={form.total_value} onChange={e => setForm({ ...form, total_value: e.target.value })} required /></div>
            <div><Label>Token Amount *</Label><Input type="number" value={form.token_amount} onChange={e => setForm({ ...form, token_amount: e.target.value })} required data-testid="booking-token-amount" /></div>
            <div>
              <Label>Payment Mode</Label>
              <Select value={form.token_mode} onValueChange={v => setForm({ ...form, token_mode: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="NEFT">NEFT</SelectItem>
                  <SelectItem value="RTGS">RTGS</SelectItem>
                  <SelectItem value="UPI">UPI</SelectItem>
                  <SelectItem value="Cheque">Cheque</SelectItem>
                  <SelectItem value="Cash">Cash</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2"><Label>UTR / Reference</Label><Input value={form.token_utr} onChange={e => setForm({ ...form, token_utr: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="submit-booking-btn">{saving ? 'Creating...' : 'Create Booking'}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
